/**
 * Profile Data Hook
 * Wraps gamification store with a cleaner interface for the profile page
 */

import { useEffect, useMemo, useState } from 'react';
import { useGamificationStore, BADGES } from '../../../../stores/gamification-store';
import type {
  UseProfileDataReturn,
  ProfileStats,
  ProfileHeroStats,
  Badge,
  EarnedBadge,
  LevelIconType,
} from '../types';

export function useProfileData(): UseProfileDataReturn {
  const store = useGamificationStore();
  const [isLoading, setIsLoading] = useState(true);

  // Sync from backend on mount
  useEffect(() => {
    const sync = async () => {
      try {
        setIsLoading(true);
        await store.syncFromBackend();
      } catch (err) {
        console.error('Failed to sync gamification data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stats
  const stats: ProfileStats = useMemo(() => ({
    xp: store.xp,
    level: store.level,
    streakDays: store.streakDays,
    totalAsIsScans: store.totalAsIsScans,
    referralCount: store.referralCount,
    bonusWatchlistSlots: store.bonusWatchlistSlots,
  }), [
    store.xp,
    store.level,
    store.streakDays,
    store.totalAsIsScans,
    store.referralCount,
    store.bonusWatchlistSlots,
  ]);

  // Level info
  const currentLevelInfo = store.getCurrentLevelInfo() || null;
  const nextLevelInfo = store.getNextLevelInfo() || null;
  const xpProgress = store.getXpProgress();

  // Hero stats
  const heroStats: ProfileHeroStats = useMemo(() => ({
    currentLevel: store.level,
    totalXp: store.xp,
    dayStreak: store.streakDays,
    referrals: store.referralCount,
  }), [store.level, store.xp, store.streakDays, store.referralCount]);

  // All badges from constants
  const allBadges: Badge[] = useMemo(() => {
    return Object.values(BADGES);
  }, []);

  // Earned badges from store
  const earnedBadges: EarnedBadge[] = useMemo(() => {
    return store.badges
      .filter((b): b is typeof b & { earnedAt: string } => !!b.earnedAt)
      .map(b => ({
        id: b.id,
        name: allBadges.find(ab => ab.id === b.id)?.name || b.id,
        description: allBadges.find(ab => ab.id === b.id)?.description || '',
        icon: allBadges.find(ab => ab.id === b.id)?.icon || 'award',
        earnedAt: b.earnedAt,
      }));
  }, [store.badges, allBadges]);

  const earnedCount = earnedBadges.length;

  // Check if badge is earned
  const isBadgeEarned = (badgeId: string): boolean => {
    return store.badges.some(b => b.id === badgeId);
  };

  // Get badge earned date
  const getBadgeEarnedDate = (badgeId: string): string | null => {
    const badge = store.badges.find(b => b.id === badgeId);
    if (badge?.earnedAt) {
      return new Date(badge.earnedAt).toLocaleDateString();
    }
    return null;
  };

  // Get level icon type
  const getLevelIcon = (): LevelIconType => {
    return store.getLevelIcon();
  };

  return {
    stats,
    currentLevelInfo,
    nextLevelInfo,
    xpProgress,
    allBadges,
    earnedBadges,
    earnedCount,
    heroStats,
    getLevelIcon,
    isBadgeEarned,
    getBadgeEarnedDate,
    isLoading,
  };
}
